/**
 * Adapter for anomaly overlay plots (base line + marker overlay).
 *
 * Maps backend `PlotData` (multi-trace) onto `MultiSeriesWebGLChart` so the
 * chart matches the rest of the app: webgl-plot rendering, shared zoom/pan,
 * axis labels in the standard format, no embedded title or legend. In the
 * threshold modal the chart participates in `sharedBrushRange` sync; in the
 * results section it renders standalone.
 */
import React, { useMemo } from 'react';

import { MultiSeriesWebGLChart, MultiSeriesSpec } from '@/shared/components/charts/WebGLChart';

import { PlotData, PlotTrace } from '../types';

interface Props {
  plot: PlotData;
  height?: number;
  /** When true, suppress the chart's own Reset Zoom button (caller provides
   *  its own — e.g. the threshold modal's modal-level button). */
  hideControls?: boolean;
  sharedBrushRange?: { startTime: number; endTime: number } | null;
  onSharedBrushChange?: (range: { startTime: number; endTime: number } | null) => void;
}

const LINE_DEFAULT_COLOR = '#14B8A6';
const SCATTER_DEFAULT_COLOR = '#ef4444';

function traceToSpec(trace: PlotTrace, index: number): MultiSeriesSpec {
  const isMarker = trace.mode === 'markers';
  const mode: 'line' | 'scatter' = isMarker ? 'scatter' : 'line';
  const fallback = isMarker ? SCATTER_DEFAULT_COLOR : LINE_DEFAULT_COLOR;

  const len = Math.min(trace.x.length, trace.y.length);
  const data = new Array(len);
  for (let i = 0; i < len; i++) {
    data[i] = { x: trace.x[i] as string | number, y: trace.y[i] ?? null };
  }

  return {
    label: trace.label ?? `Series ${index + 1}`,
    mode,
    color: trace.color ?? fallback,
    data,
  };
}

const AnomalyOverlayChartImpl: React.FC<Props> = ({
  plot,
  height,
  hideControls,
  sharedBrushRange,
  onSharedBrushChange,
}) => {
  const series = useMemo(
    () => plot.traces.map((t, i) => traceToSpec(t, i)),
    [plot.traces],
  );

  return (
    <MultiSeriesWebGLChart
      series={series}
      yLabel={plot.yaxis.label}
      height={height}
      hideControls={hideControls}
      sharedBrushRange={sharedBrushRange}
      onSharedBrushChange={onSharedBrushChange}
    />
  );
};

const AnomalyOverlayChart = React.memo(AnomalyOverlayChartImpl);
export default AnomalyOverlayChart;
